/**
 * Sign in or sign up with an email magic link (BR-001, BR-002).
 *
 * One form for both: the email, plus an invite code that only new learners
 * need. The backend answers the same way whatever happens, so the "check your
 * email" screen can't say whether an email was sent; it points new learners at
 * the invite code instead.
 */
import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { apiFetch, errorMessageFrom } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { Button } from '../ui/Button';
import { ErrorBanner } from '../ui/ErrorBanner';
import styles from './SignIn.module.css';

export const RESEND_COOLDOWN_SECONDS = 60;

export const SignInPage: React.FC = () => {
  const { state } = useAuth();
  const [email, setEmail] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [showInvite, setShowInvite] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = window.setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);

  if (state.status === 'signed-in') {
    return <Navigate to="/" replace />;
  }

  const requestLink = async (): Promise<void> => {
    setSending(true);
    setError(null);
    try {
      const body: Record<string, string> = { email: email.trim() };
      if (inviteCode.trim()) body.invite_code = inviteCode.trim();
      const response = await apiFetch(
        '/auth/request-link',
        { method: 'POST', body: JSON.stringify(body) },
        { notifyUnauthorized: false }
      );
      if (!response.ok) {
        setError(
          response.status === 422
            ? 'Enter a valid email address.'
            : await errorMessageFrom(response)
        );
        return;
      }
      setSentTo(email.trim());
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch {
      setError("We couldn't reach the server. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const onSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    void requestLink();
  };

  if (sentTo) {
    return (
      <main className={styles.page}>
        <div className={styles.card}>
          <h1 className={styles.title}>Check your email</h1>
          <p className={styles.lead}>
            We sent a sign-in link to <strong>{sentTo}</strong>. It works once
            and expires in 15 minutes.
          </p>
          <p className={styles.hint}>
            No email after a minute? New learners need an invite code.{' '}
            <button
              type="button"
              className={styles.linkButton}
              onClick={() => {
                setSentTo(null);
                setShowInvite(true);
              }}
            >
              Enter an invite code
            </button>
          </p>
          {error && <ErrorBanner message={error} className={styles.banner} />}
          <Button
            variant="secondary"
            disabled={cooldown > 0 || sending}
            loading={sending}
            onClick={() => void requestLink()}
          >
            {cooldown > 0 ? `Send again in ${cooldown}s` : 'Send again'}
          </Button>
          <button
            type="button"
            className={styles.linkButton}
            onClick={() => setSentTo(null)}
          >
            Use a different email
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className={styles.page}>
      <div className={styles.card}>
        <h1 className={styles.title}>Sign in</h1>
        <p className={styles.lead}>
          Enter your email and we&apos;ll send you a link to sign in.
        </p>
        <form className={styles.form} onSubmit={onSubmit}>
          <label className={styles.label}>
            Email
            <input
              className={styles.input}
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          {showInvite ? (
            <label className={styles.label}>
              Invite code
              <input
                className={styles.input}
                autoComplete="off"
                maxLength={64}
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value)}
              />
            </label>
          ) : (
            <button
              type="button"
              className={styles.linkButton}
              onClick={() => setShowInvite(true)}
            >
              New here? I have an invite code
            </button>
          )}
          {error && <ErrorBanner message={error} className={styles.banner} />}
          <Button type="submit" disabled={sending} loading={sending}>
            Send me a link
          </Button>
        </form>
      </div>
    </main>
  );
};

export default SignInPage;
